import React, { useRef, useImperativeHandle, useEffect, useState } from 'react';
import _merge from 'lodash/merge';
import List, { ListProps } from 'components/List';
import Menu, { MenuRef } from 'components/Menu';
import { getMenuMaxHeight } from 'common/MenuDimensionCalc';
import { getContext } from 'common';
import { SPACING_8 } from '@nokia-csf-uxr/freeform-design-tokens/tokens/spacing';
import sameWidth from './config/sameWidth';

const defaultPositioningOptions = {
  placement: 'bottom',
  modifiers: [{ name: 'offset', options: { offset: [0, -64] } }, sameWidth]
};

export interface BreadcrumbCompactsListProps extends React.ComponentPropsWithoutRef<'div'> {
  /** Children prop is where compact list content resides. */
  children: React.ReactNode;
  /** If true, the list will be expanded */
  isOpen?: boolean;
  /** Props which will be passed into List component */
  listProps?: Partial<ListProps>;
  /** Any element ref that triggers the menu. Use when the trigger element is not the default IconButton */
  triggerEl?: React.RefObject<HTMLElement>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  style?: any;
}

export type BreadcrumbCompactsListHandle = {
  menuRef?: React.RefObject<MenuRef>,
  positionMenu: (any) => void
};

/**
 * Single BreadcrumbCompactsList.
 * Any other props than `children` and `listProps` will be provided to the wrapped Menu component. */
const BreadcrumbCompactsList = React.forwardRef<BreadcrumbCompactsListHandle, BreadcrumbCompactsListProps>((props: BreadcrumbCompactsListProps, ref) =>
  getContext(({ theme }: { theme?: any }) => {
    const {
      children,
      listProps,
      isOpen,
      style,
      triggerEl,
      theme: themeProp,
      ...otherProps
    } = props;
    const menuRef = useRef<MenuRef>(null);
    const [listMaxHeight, setListMaxHeight] = useState(null);
    const resolvedTheme = _merge({}, theme ? theme.BREADCRUMBS : {}, themeProp);

    useImperativeHandle(ref, () => ({
      menuRef,
      positionMenu: () => {
        menuRef.current && menuRef.current.positionMenu();
      }
    }));

    useEffect(() => {
      setListMaxHeight(getMenuMaxHeight(triggerEl.current, '100vh'));
    }, [triggerEl.current]);

    const resolvedPopupProps = {
      positioningOptions: defaultPositioningOptions,
      ref: menuRef,
      usePortal: true,
      triggerEl,
      isOpen,
      style: { backgroundColor: resolvedTheme.background },
      ...otherProps
    };

    return (
      <Menu {...resolvedPopupProps}>
        <List
          style={{
            padding: `0 0 ${SPACING_8} 0`,
            maxHeight: listMaxHeight,
            backgroundColor: resolvedTheme.background
          }}
          truncateListText
          {...listProps}
        >
          {children}
        </List>
      </Menu>
    );
  }));

BreadcrumbCompactsList.displayName = 'BreadcrumbCompactsList';

BreadcrumbCompactsList.defaultProps = {
  listProps: {},
  triggerEl: { current: undefined },
  isOpen: false,
  theme: {},
  style: {},
};

export default BreadcrumbCompactsList;
